/**
 * SupportSessionsTable.tsx
 * ----------------------------------------------------------------------------
 * Tabela compartilhada para listar sessões de suporte WL.
 * Usada em três superfícies: aba do modal de tenant, página /wl/sessions e
 * página /admin/sessions. A coluna "Tenant" pode ser ocultada (modal).
 *
 * RELEASE: 2026-05-30 v1.1.0 - Padronização visual com WlTenants (text-xs/py-2,
 *                              DotLabel para status, fullBleed sem border) +
 *                              nova coluna "Encerramento" (ended_reason).
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, KeyRound, Eye, ShieldAlert, Clock, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import type { SupportSessionRow, SupportSessionStatus } from "@/hooks/wl/useSupportSessionsAudit";

// ---------- Helpers ----------
const fmtDt = (v: string | null) =>
  v ? format(new Date(v), "dd/MM/yy HH:mm:ss", { locale: ptBR }) : "—";

const fmtDuration = (sec: number) => {
  if (!sec || sec < 0) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
};

const STATUS_LABEL: Record<SupportSessionStatus, string> = {
  active: "Ativa",
  ended: "Encerrada",
  expired: "Expirada",
};

// Mantido para compatibilidade com SupportSessionDetailsDrawer (Badge no header)
const STATUS_TONE: Record<SupportSessionStatus, string> = {
  active: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-300",
  ended: "bg-muted text-muted-foreground border-border",
  expired: "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300",
};

// Bolinha colorida do status (padrão WlTenants)
const STATUS_DOT: Record<SupportSessionStatus, string> = {
  active: "bg-emerald-500",
  ended: "bg-zinc-400",
  expired: "bg-amber-500",
};

// Tradução amigável de ended_reason
const ENDED_REASON_LABEL: Record<string, string> = {
  manual: "Manual",
  expired: "Expirada",
  replaced: "Substituída",
  switched: "Substituída",
  timeout: "Timeout",
};

function endedReasonLabel(raw: string | null): string {
  if (!raw) return "—";
  return ENDED_REASON_LABEL[raw] ?? raw;
}

/** Bolinha colorida + texto — padrão visual de status do sistema (WlTenants). */
function DotLabel({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground whitespace-nowrap">
      <span className={cn("h-2 w-2 rounded-full shrink-0", color)} />
      {label}
    </span>
  );
}

interface SupportSessionsTableProps {
  rows: SupportSessionRow[];
  loading?: boolean;
  showTenantColumn?: boolean;
  onRowClick?: (row: SupportSessionRow) => void;
  emptyMessage?: string;
}

export function SupportSessionsTable({
  rows,
  loading,
  showTenantColumn = true,
  onRowClick,
  emptyMessage = "Nenhuma sessão de suporte encontrada.",
}: SupportSessionsTableProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando sessões…
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground gap-2">
        <KeyRound className="h-8 w-8 opacity-40" />
        <p className="text-sm">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="text-xs font-medium">Status</TableHead>
          <TableHead className="text-xs font-medium">Operador</TableHead>
          {showTenantColumn && <TableHead className="text-xs font-medium">Tenant</TableHead>}
          <TableHead className="text-xs font-medium">Motivo</TableHead>
          <TableHead className="text-xs font-medium whitespace-nowrap">Início</TableHead>
          <TableHead className="text-xs font-medium whitespace-nowrap">Fim</TableHead>
          <TableHead className="text-xs font-medium whitespace-nowrap">Encerramento</TableHead>
          <TableHead className="text-xs font-medium whitespace-nowrap">Duração</TableHead>
          <TableHead className="text-xs font-medium text-center">Ações</TableHead>
          <TableHead className="w-[40px]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow
            key={r.session_id}
            className={cn(onRowClick && "cursor-pointer hover:bg-muted/50")}
            onClick={() => onRowClick?.(r)}
          >
            <TableCell className="py-2">
              <DotLabel color={STATUS_DOT[r.status]} label={STATUS_LABEL[r.status]} />
            </TableCell>

            <TableCell className="py-2 text-xs">
              <div className="text-foreground font-medium truncate max-w-[180px]">
                {r.operator_display_name || "—"}
              </div>
              {r.operator_email && (
                <div className="text-[11px] text-muted-foreground truncate max-w-[180px]">
                  {r.operator_email}
                </div>
              )}
            </TableCell>

            {showTenantColumn && (
              <TableCell className="py-2 text-xs">
                <div className="text-foreground font-medium truncate max-w-[180px]">
                  {r.tenant_name || "—"}
                </div>
                {r.tenant_slug && (
                  <div className="text-[11px] text-muted-foreground font-mono truncate max-w-[180px]">
                    {r.tenant_slug}
                  </div>
                )}
              </TableCell>
            )}

            <TableCell className="py-2 text-xs max-w-[260px]">
              <div className="flex items-start gap-1.5">
                <ShieldAlert className="h-3.5 w-3.5 mt-0.5 text-amber-500 shrink-0" />
                <span className="text-foreground line-clamp-2">{r.reason || "—"}</span>
              </div>
            </TableCell>

            <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
              {fmtDt(r.started_at)}
            </TableCell>

            <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
              {r.ended_at ? (
                fmtDt(r.ended_at)
              ) : r.status === "expired" ? (
                <span className="text-amber-600">{fmtDt(r.expires_at)}</span>
              ) : (
                <span className="text-emerald-600">Em andamento</span>
              )}
            </TableCell>

            <TableCell className="py-2 text-xs text-foreground whitespace-nowrap">
              {endedReasonLabel(r.ended_reason)}
            </TableCell>

            <TableCell className="py-2 text-xs whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3 text-muted-foreground" />
                {fmtDuration(r.duration_seconds)}
              </span>
            </TableCell>

            <TableCell className="py-2 text-xs text-center">
              <span className="inline-flex items-center gap-1">
                <Activity className="h-3 w-3 text-muted-foreground" />
                {r.actions_count}
              </span>
            </TableCell>

            <TableCell className="py-2">
              {onRowClick && (
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <Eye className="h-3.5 w-3.5" />
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Re-export helpers para uso nas páginas e no drawer
export { fmtDt, fmtDuration, STATUS_LABEL, STATUS_TONE, endedReasonLabel };
